import { useState } from 'react';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { useCurrentSite } from '@hotjar/site';
import type { UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';
import { getFeaturedInsightsContent, getFeaturedInsightThumbnails } from './api';
import type { FeaturedInsightsContent, Thumbnail, ThumbnailUrl } from './types';

const FEATURED_INSIGHTS_CONTENT_QUERY_KEY = 'featured_insights_content';
const FEATURED_INSIGHTS_THUMBNAIL_CONTENT_QUERY_KEY = 'featured_insights_thumbnail_content';
const FEATURED_INSIGHTS_THUMBNAIL_REFETCH_TIME = 5000;
const FEATURED_INSIGHTS_CONTENT_STALE_TIME = 3000;

export const invalidateFeaturedInsightsContentQuery = (): void => {
  queryClient.invalidateQueries([FEATURED_INSIGHTS_CONTENT_QUERY_KEY]);
};

export const useFeaturedInsightsContent = (): UseQueryResult<FeaturedInsightsContent, Error> => {
  const { data: currentSite } = useCurrentSite();
  const enabled = currentSite?.id !== undefined;

  return useQuery({
    queryKey: [FEATURED_INSIGHTS_CONTENT_QUERY_KEY, { siteId: currentSite?.id }],
    queryFn: () => {
      if (!enabled) throw new Error();
      return getFeaturedInsightsContent(currentSite?.id);
    },
    enabled,
    staleTime: FEATURED_INSIGHTS_CONTENT_STALE_TIME,
    refetchOnWindowFocus: false,
  });
};

const refetchTiming = (thumbnailData: Thumbnail | undefined) => {
  if (thumbnailData && (thumbnailData.status === 'success' || thumbnailData.status === 'failure')) {
    return false;
  }

  return FEATURED_INSIGHTS_THUMBNAIL_REFETCH_TIME;
};

export const useFeaturedInsightsThumbnails = (
  thumbnailUrl?: ThumbnailUrl,
): UseQueryResult<Thumbnail, Error> => {
  const [thumbnailData, setThumbnailData] = useState<Thumbnail | undefined>(undefined);

  return useQuery({
    queryKey: [FEATURED_INSIGHTS_THUMBNAIL_CONTENT_QUERY_KEY, { thumbnailId: thumbnailUrl }],
    queryFn: () => {
      return getFeaturedInsightThumbnails(thumbnailUrl);
    },
    onSuccess: (data) => setThumbnailData(data),
    refetchInterval: refetchTiming(thumbnailData),
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });
};
